import { ReactElement, useEffect, useState } from 'react';
import { getLabelFallback } from '@utils/utils';
import { useDispatch, useSelector } from 'react-redux';
import * as actions from '@redux-actions/';
import Cookies from 'js-cookie';

import Loader from '@components/Loader';

type InsuranceProps = {
    id: string;
}

const Insurance = ({ id } : InsuranceProps) : ReactElement => {
    const dispatch = useDispatch();

    const config = useSelector(state => state.cart.config);
    const formattedOrderItems = useSelector(state => state.cart.formattedOrderItems);
    const insurance = useSelector(state => state.cart.insurance);
    const [cartItemsNumber, setCartItemsNumber] = useState(0);

    const [isLoadingInsurance, setIsLoadingInsurance] = useState(false);
    const [isLoadedInsurance, setIsLoadedInsurance] = useState(false);
    const [refreshCart, setRefreshCart] = useState(false);
    const [hasError, setHasError] = useState(null);
    const [isNotInsurable, setIsNotInsurable] = useState(false);

    
    const extractItemType = () => {
        if (!Array.isArray(formattedOrderItems)) return [];
        const types = formattedOrderItems.map(item => {
            const pres = {
                prescription: !!item.frame.xitem_prescriptionComplete ? item.frame.xitem_prescriptionComplete : null
            }
            return {
                type: item.frame.xitem_frameType,
                ...pres
            }
        });
        return types.filter((v,i,a)=>a.findIndex(t=>['type','prescription'].every(k=>t[k] ===v[k]))===i);
    }

    const isNotInsurableOrder = () => {
            const items = extractItemType();

            const hasRxNoPresc = items.some(item => item.type === 'NO PRESCRIPTION');

            return (hasRxNoPresc);

    }

    const callInsurance = () => {
        if(config.cartLensUpsell.enabled === true){
            dispatch(actions.startLoading());
        }
        const ria_0_cookie = Cookies.get('ria_0');
        const ria_1_cookie = Cookies.get('ria_1');

        if(formattedOrderItems.length === 1) {
            //if the insurance is not loaded, add insurance process
            if(isLoadedInsurance === false) {
                setIsLoadingInsurance(true);
                let prod2Hierarchy=formattedOrderItems[0].moCo.prod_hierarchy_2;
                let isSafetyProduct=false;
                if(prod2Hierarchy!=undefined && (prod2Hierarchy === 'Safety Eyeglasses' || prod2Hierarchy === 'On-Duty Sunglasses')){
                    isSafetyProduct=true;
                }

                if(!!ria_0_cookie || !!ria_1_cookie) {
                    config.callFromComponentToGl.onRecalculateInsurance();
                } else {
                    //all possible items insurable, call modal insurance
                    if(
                        config.insurance.rxInsurable === true &&
                        config.insurance.frameOnlyInsurable === true &&
                        config.insurance.noPrescriptionInsurable === true
                    ) {
                        config.callFromComponentToGl.onInsuranceOpening(isSafetyProduct);

                    //all possible items not insurable, set error
                    } else if(
                        config.insurance.rxInsurable === false &&
                        config.insurance.frameOnlyInsurable === false &&
                        config.insurance.noPrescriptionInsurable === false
                    ) {
                        setHasError('INSURANCE_NOT_APPLICABLE_ERROR');
                        setIsNotInsurable(false);
                        setIsLoadingInsurance(false);
                        setIsLoadedInsurance(false);
                    //control type of items for applying insurance
                    } else {
                        const items = extractItemType();

                        const frameOnly = items.find(item => item.type === 'FRAME ONLY');
                        const rxNoPresc = items.find(item => (item.type === 'NO PRESCRIPTION'));
                        const rxWithPresc = items.find(item => (item.type === 'RX'));

                        let loadInsurance = false;
                        if(config.insurance.frameOnlyInsurable === true) {
                            loadInsurance = loadInsurance || !!frameOnly;
                        }

                        if(config.insurance.rxInsurable === true) {
                            loadInsurance = loadInsurance || !!rxWithPresc;
                        } 

                        if(config.insurance.noPrescriptionInsurable === true) {
                            loadInsurance = loadInsurance || !!rxNoPresc;
                        }

                        if(loadInsurance) {
                            config.callFromComponentToGl.onInsuranceOpening(isSafetyProduct);
                        } else {
                            setHasError('INSURANCE_NOT_APPLICABLE_ERROR');
                            setIsLoadingInsurance(false);                          
                            setIsLoadedInsurance(false);
                        }

                        if(!!rxNoPresc){
                            setIsNotInsurable(true);
                        } else{
                            setHasError('INSURANCE_NOT_APPLICABLE_ERROR');
                            setIsNotInsurable(false);
                        }
                    }
                }
                            
                //if the insurance is already loaded, remove insurance process
            } else if(isLoadedInsurance === true) {
                setIsLoadingInsurance(true);
                config.callFromComponentToGl.onRecalculateInsurance();
            }
        } else {
            if(config.cartLensUpsell.enabled === true){
                dispatch(actions.endLoading());
            } 
            setHasError('too_many_products');
            dispatch(actions.setInsuranceError(true));
            setIsLoadingInsurance(false);
            setIsLoadedInsurance(false);
        }         
    }

    useEffect(() => {
        if(refreshCart === true && hasError === null) {
            dispatch(actions.getCart(config.mockRequests, config.hostname));
            setIsNotInsurable(isNotInsurableOrder());
        }
        setRefreshCart(false);
    }, [refreshCart, hasError])

  useEffect(() => {
    if (!!formattedOrderItems) {

      if (formattedOrderItems.length >= cartItemsNumber) {
        // Item added to the cart
        if (cartItemsNumber === 0) {
          // First time enter in method, cartItemsNumber is 0 for sure
          // One item added to the cart
          if (!!formattedOrderItems.find(item => item.frame.hasInsuranceApplied === true)) {
            // Insurance already set
            dispatch(actions.setInsuranceError(false));
            setIsLoadingInsurance(false);
            setIsLoadedInsurance(true);
            setHasError(null);
          }
          
        } else if (cartItemsNumber === 1){
          // Only one item in cart
            setIsLoadedInsurance(
              formattedOrderItems.find(item => item.frame.hasInsuranceApplied === true && item.frame.hasInsuranceCalculated === true) ? true : false
            );
        }else{
            // Two or more items added to the cart
            if (!!formattedOrderItems.find(item => item.frame.hasInsuranceApplied === true) || (!!Cookies.get('ria_1') && !!Cookies.get('json_insurance'))) {
                // Insurance already set received by cart/@self or by cookie enabled
                setHasError('too_many_products');
                dispatch(actions.setInsuranceError(true));
                setIsLoadingInsurance(false);
                setIsLoadedInsurance(false);
            }
        }
      } else if (formattedOrderItems.length < cartItemsNumber) {
        // Item removed from cart
        if ( formattedOrderItems.length === 1 ) {
          // Only one item left in cart
          if (hasError === 'too_many_products') {
            // Se si era provato a inserire l'insurance con più item
            dispatch(actions.setInsuranceError(false));
            setIsLoadedInsurance(false);
            setIsLoadingInsurance(true);
            setHasError(null);
            config.callFromComponentToGl.calculateInsuranceOnLoading();
          }
        } else {
          if (hasError === 'too_many_products') {
            setHasError('too_many_products');
            dispatch(actions.setInsuranceError(true));
            setIsLoadingInsurance(false);
            setIsLoadedInsurance(false);
          }
        }
      }

      setCartItemsNumber(formattedOrderItems.length);
    }
  }, [formattedOrderItems]);


    useEffect(() => {
       if(insurance !== undefined) {
            if(insurance !== null) {
                setIsLoadingInsurance(false);
                setIsLoadedInsurance(insurance);
                setHasError(insurance === false && !isNotInsurable? 'INSURANCE_ERROR_LABEL' : null);
                setRefreshCart(true);    
            } else {
                setIsLoadingInsurance(false);
            }
       }
       
    }, [insurance]);

    const insuranceLabel = isLoadingInsurance && !isLoadedInsurance ? getLabelFallback('LOADING_INSURANCE_LABEL') :
            isLoadingInsurance && isLoadedInsurance ? getLabelFallback('REMOVE_INSURANCE_LABEL'):
            isLoadedInsurance ? getLabelFallback('INSURANCE_LOADED_LABEL') :
            hasError && hasError !== 'too_many_products' ? getLabelFallback(hasError):
            getLabelFallback('USE_INSURANCE_LABEL');

    const classes = isLoadingInsurance || isLoadedInsurance ? 'green' : !!hasError && hasError !== 'too_many_products' ? 'error-insurance' : '';

    return(
        <div className={`insurance ${classes}`}>
            <div className="insurance-main-content">
                <div className="insurance-content">
                    <span className="insurance-label">{ insuranceLabel } {isLoadedInsurance && <span className="view-benefits title" onClick={() => config.callFromComponentToGl.onInsuranceOpening(false)}>{getLabelFallback('INSURANCE_LOADED_VIEW_BENEFITS')}</span>}
                    </span>
                    {isLoadingInsurance &&
                        <span className="insurance-info">{ getLabelFallback('LOADING_INSURANCE_CLAIM') }</span>
                    }
                </div>
                
                {!isLoadingInsurance &&
                    <>
                        <label className={`toggle-button-checkbox ${classes}`}>
                            <label style={{visibility: 'hidden'}} htmlFor={id}>{getLabelFallback('INSURANCE_LABEL')}</label>
                            <input id={id} type="checkbox" onClick={() => callInsurance()}/>
                            <span className="checkbox-checkmark-insurance"></span>
                        </label>
                    </>
                    
                }

                {isLoadingInsurance &&
                    <Loader classes={"green"}/>
                }
            </div>
            <div className={`notinsurable ${isNotInsurable && isLoadedInsurance ? '' : 'hide'}`}>
                <span>{getLabelFallback('ITEM_NOT_INSURABLE_LABEL')}</span>
            </div>
        </div>
    )
}

export default Insurance;
